import React, { useCallback, useContext } from "react"
import { useParams, useNavigate, useLocation } from "react-router"

import { useLocalization } from "@izettle/tenko-react"

import { ENV_SERVICE_URL_DEFAULT } from "@/config/environment"
import { zettleBrandLogoPrimaryPositive } from "@/core/assets/graphics"
import { ConfigContext } from "@/core/context/config"
import { OPT_OUT_FAILURE_STATUS, OPT_OUT_SUCCESS_STATUS } from "@/core/utils/constant"
import { OptOutFooter } from "@/modules/opt-out/components/faq-footer"
import { useOptOutTracking } from "@/modules/opt-out/hooks/tracking"
import { clearReceiptOwnerContactInformation } from "@/modules/opt-out/service"
import {
  OptOutBox,
  OptOutText,
  MainTitle,
  OptOutButton,
  OptOutBoxContent,
  Divider,
  Logo,
  PageContainer,
} from "@/modules/opt-out/styles"
import { OptOutResponse } from "@/types"

export const DigitalReceiptsOptOut = () => {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const region = queryParams.get("region") ?? ENV_SERVICE_URL_DEFAULT

  useOptOutTracking(id)
  const { text } = useLocalization()
  const navigate = useNavigate()
  const config = useContext(ConfigContext)

  const optOutCallback = useCallback(
    (response: OptOutResponse) => {
      const getStatusURL = (status: string) =>
        `/receipt/${id}/opt-status?status=${status}`
      const optOutStatusPath =
        response.code === 204
          ? `${getStatusURL(OPT_OUT_SUCCESS_STATUS)}`
          : `${getStatusURL(OPT_OUT_FAILURE_STATUS)}&reason=${response.code}`
      navigate(optOutStatusPath)
    },
    [navigate],
  )

  const clearButtonClicked = useCallback(() => {
    if (id) {
      clearReceiptOwnerContactInformation(id, region)(optOutCallback)
    }
  }, [id, region, optOutCallback])

  let paypalServiceNote
  const localeCountry = config.locale?.territory.toLowerCase()
  if (localeCountry !== "mx" && localeCountry !== "br") {
    paypalServiceNote = (
      <OptOutText>{text("OPT_OUT_CONTENT_PAYPAL_SERVICE_NOTE")}</OptOutText>
    )
  }
  return (
    <PageContainer>
      <Logo src={zettleBrandLogoPrimaryPositive} alt="Logo" />
      <OptOutBox className="App">
        <OptOutBoxContent>
          <MainTitle>{text("OPT_OUT_TITLE")}</MainTitle>
          <OptOutText>{text("OPT_OUT_CONTENT")}</OptOutText>
          {paypalServiceNote}
          <OptOutButton
            onClick={clearButtonClicked}
            type="button"
            data-track-id="opt-out-button"
          >
            {text("OPT_OUT_BUTTON")}
          </OptOutButton>
          <Divider />
          <OptOutFooter />
        </OptOutBoxContent>
      </OptOutBox>
    </PageContainer>
  )
}
